import { MetricField } from '@/__generated__/graphql';
import { type I18n } from '@lingui/core';
import { t } from '@lingui/macro';

export type MetricFieldAttributes = {
  value: MetricField;
  dataLabel: string;
  lastDataLabel: string;
  category: 'basic' | 'advanced';
};

type AttributesMap = Record<MetricField, MetricFieldAttributes>;

const getAttributesMap = (i18n: I18n): AttributesMap => ({
  [MetricField.AccumulatedVol]: {
    value: MetricField.AccumulatedVol,
    dataLabel: t(i18n)`volumen acumulado`,
    lastDataLabel: t(i18n)`último volumen acumulado`,
    category: 'advanced',
  },
  [MetricField.AccumulatedVolume]: {
    value: MetricField.AccumulatedVolume,
    dataLabel: t(i18n)`volumen acumulado`,
    lastDataLabel: t(i18n)`último volumen acumulado`,
    category: 'basic',
  },
  [MetricField.AccumulatedVolRaw]: {
    value: MetricField.AccumulatedVolRaw,
    dataLabel: t(i18n)`volumen acumulado bruto`,
    lastDataLabel: t(i18n)`último volumen acumulado bruto`,
    category: 'advanced',
  },
  [MetricField.Allocation]: {
    value: MetricField.Allocation,
    dataLabel: t(i18n)`dotación`,
    lastDataLabel: t(i18n)`última dotación`,
    category: 'advanced',
  },
  [MetricField.Angle]: {
    value: MetricField.Angle,
    dataLabel: t(i18n)`ángulo`,
    lastDataLabel: t(i18n)`último ángulo`,
    category: 'advanced',
  },
  [MetricField.AngleNonCalibrated]: {
    value: MetricField.AngleNonCalibrated,
    dataLabel: t(i18n)`ángulo no calibrado`,
    lastDataLabel: t(i18n)`último ángulo no calibrado`,
    category: 'advanced',
  },
  [MetricField.AngleNonCalibratedRaw]: {
    value: MetricField.AngleNonCalibratedRaw,
    dataLabel: t(i18n)`ángulo no calibrado bruto`,
    lastDataLabel: t(i18n)`último ángulo no calibrado bruto`,
    category: 'advanced',
  },
  [MetricField.BatterySoc]: {
    value: MetricField.BatterySoc,
    dataLabel: t(i18n)`estado de carga de batería`,
    lastDataLabel: t(i18n)`último estado de carga de batería`,
    category: 'advanced',
  },
  [MetricField.BatterySocRaw]: {
    value: MetricField.BatterySocRaw,
    dataLabel: t(i18n)`estado de carga de batería bruto`,
    lastDataLabel: t(i18n)`último estado de carga de batería bruto`,
    category: 'advanced',
  },
  [MetricField.BatteryV]: {
    value: MetricField.BatteryV,
    dataLabel: t(i18n)`voltaje de batería`,
    lastDataLabel: t(i18n)`último voltaje de batería`,
    category: 'advanced',
  },
  [MetricField.BatteryVRaw]: {
    value: MetricField.BatteryVRaw,
    dataLabel: t(i18n)`voltaje de batería bruto`,
    lastDataLabel: t(i18n)`último voltaje de batería bruto`,
    category: 'advanced',
  },
  [MetricField.CalculatedAngle]: {
    value: MetricField.CalculatedAngle,
    dataLabel: t(i18n)`ángulo calculado`,
    lastDataLabel: t(i18n)`último ángulo calculado`,
    category: 'advanced',
  },
  [MetricField.CalculatedTotalVolume]: {
    value: MetricField.CalculatedTotalVolume,
    dataLabel: t(i18n)`volumen total calculado`,
    lastDataLabel: t(i18n)`último volumen total calculado`,
    category: 'advanced',
  },
  [MetricField.CalculatedVolume]: {
    value: MetricField.CalculatedVolume,
    dataLabel: t(i18n)`volumen calculado`,
    lastDataLabel: t(i18n)`último volumen calculado`,
    category: 'advanced',
  },
  [MetricField.ChargeI]: {
    value: MetricField.ChargeI,
    dataLabel: t(i18n)`corriente de carga`,
    lastDataLabel: t(i18n)`última corriente de carga`,
    category: 'advanced',
  },
  [MetricField.ChargeIRaw]: {
    value: MetricField.ChargeIRaw,
    dataLabel: t(i18n)`corriente de carga bruta`,
    lastDataLabel: t(i18n)`última corriente de carga bruta`,
    category: 'advanced',
  },
  [MetricField.ChargeP]: {
    value: MetricField.ChargeP,
    dataLabel: t(i18n)`potencia de carga`,
    lastDataLabel: t(i18n)`última potencia de carga`,
    category: 'advanced',
  },
  [MetricField.ChargePRaw]: {
    value: MetricField.ChargePRaw,
    dataLabel: t(i18n)`potencia de carga bruta`,
    lastDataLabel: t(i18n)`última potencia de carga bruta`,
    category: 'advanced',
  },
  [MetricField.Conductivity]: {
    value: MetricField.Conductivity,
    dataLabel: t(i18n)`conductividad`,
    lastDataLabel: t(i18n)`última conductividad`,
    category: 'basic',
  },
  [MetricField.ConductivityRaw]: {
    value: MetricField.ConductivityRaw,
    dataLabel: t(i18n)`conductividad bruta`,
    lastDataLabel: t(i18n)`última conductividad bruta`,
    category: 'advanced',
  },
  [MetricField.ConnectionState]: {
    value: MetricField.ConnectionState,
    dataLabel: t(i18n)`estado de conexión`,
    lastDataLabel: t(i18n)`último estado de conexión`,
    category: 'advanced',
  },
  [MetricField.DgaAccumulatedVolume]: {
    value: MetricField.DgaAccumulatedVolume,
    dataLabel: t(i18n)`volumen acumulado dga`,
    lastDataLabel: t(i18n)`último volumen acumulado dga`,
    category: 'advanced',
  },
  [MetricField.DgaFlow]: {
    value: MetricField.DgaFlow,
    dataLabel: t(i18n)`caudal dga`,
    lastDataLabel: t(i18n)`último caudal dga`,
    category: 'advanced',
  },
  [MetricField.DgaLevel]: {
    value: MetricField.DgaLevel,
    dataLabel: t(i18n)`nivel dga`,
    lastDataLabel: t(i18n)`último nivel dga`,
    category: 'advanced',
  },
  [MetricField.DissolvedOxygen]: {
    value: MetricField.DissolvedOxygen,
    dataLabel: t(i18n)`oxígeno disuelto`,
    lastDataLabel: t(i18n)`último oxígeno disuelto`,
    category: 'advanced',
  },
  [MetricField.DissolvedOxygenRaw]: {
    value: MetricField.DissolvedOxygenRaw,
    dataLabel: t(i18n)`oxígeno disuelto bruto`,
    lastDataLabel: t(i18n)`último oxígeno disuelto bruto`,
    category: 'advanced',
  },
  [MetricField.DissolvedOxygenSaturation]: {
    value: MetricField.DissolvedOxygenSaturation,
    dataLabel: t(i18n)`saturación de oxígeno disuelto`,
    lastDataLabel: t(i18n)`última saturación de oxígeno disuelto`,
    category: 'advanced',
  },
  [MetricField.DissolvedOxygenSaturationRaw]: {
    value: MetricField.DissolvedOxygenSaturationRaw,
    dataLabel: t(i18n)`saturación de oxígeno disuelto bruta`,
    lastDataLabel: t(i18n)`última saturación de oxígeno disuelto bruta`,
    category: 'advanced',
  },
  [MetricField.DistributionAlerts]: {
    value: MetricField.DistributionAlerts,
    dataLabel: t(i18n)`alertas de distribución`,
    lastDataLabel: t(i18n)`última alerta de distribución`,
    category: 'advanced',
  },
  [MetricField.DopplerVelocity]: {
    value: MetricField.DopplerVelocity,
    dataLabel: t(i18n)`velocidad doppler`,
    lastDataLabel: t(i18n)`última velocidad doppler`,
    category: 'advanced',
  },
  [MetricField.DopplerVelocityRaw]: {
    value: MetricField.DopplerVelocityRaw,
    dataLabel: t(i18n)`velocidad doppler bruta`,
    lastDataLabel: t(i18n)`última velocidad doppler bruta`,
    category: 'advanced',
  },
  [MetricField.EnabledRatingCurves]: {
    value: MetricField.EnabledRatingCurves,
    dataLabel: t(i18n)`curvas de aforo habilitadas`,
    lastDataLabel: t(i18n)`última curva de aforo habilitada`,
    category: 'advanced',
  },
  [MetricField.Energy]: {
    value: MetricField.Energy,
    dataLabel: t(i18n)`energía`,
    lastDataLabel: t(i18n)`última energía`,
    category: 'advanced',
  },
  [MetricField.Events]: {
    value: MetricField.Events,
    dataLabel: t(i18n)`eventos`,
    lastDataLabel: t(i18n)`último evento`,
    category: 'advanced',
  },
  [MetricField.Intruder]: {
    value: MetricField.Intruder,
    dataLabel: t(i18n)`intruso`,
    lastDataLabel: t(i18n)`último intruso`,
    category: 'advanced',
  },
  [MetricField.Flow]: {
    value: MetricField.Flow,
    dataLabel: t(i18n)`caudal`,
    lastDataLabel: t(i18n)`último caudal`,
    category: 'basic',
  },
  [MetricField.FlowAlerts]: {
    value: MetricField.FlowAlerts,
    dataLabel: t(i18n)`alertas de caudal`,
    lastDataLabel: t(i18n)`última alerta de caudal`,
    category: 'advanced',
  },
  [MetricField.FlowRaw]: {
    value: MetricField.FlowRaw,
    dataLabel: t(i18n)`caudal bruto`,
    lastDataLabel: t(i18n)`último caudal bruto`,
    category: 'advanced',
  },
  [MetricField.GateSensorCurrent]: {
    value: MetricField.GateSensorCurrent,
    dataLabel: t(i18n)`corriente del sensor de compuerta`,
    lastDataLabel: t(i18n)`última corriente del sensor de compuerta`,
    category: 'advanced',
  },
  [MetricField.GateSensorDistance]: {
    value: MetricField.GateSensorDistance,
    dataLabel: t(i18n)`distancia al sensor de compuerta`,
    lastDataLabel: t(i18n)`última distancia al sensor de compuerta`,
    category: 'advanced',
  },
  [MetricField.Level]: {
    value: MetricField.Level,
    dataLabel: t(i18n)`nivel`,
    lastDataLabel: t(i18n)`último nivel`,
    category: 'basic',
  },
  [MetricField.LevelAlerts]: {
    value: MetricField.LevelAlerts,
    dataLabel: t(i18n)`alertas de nivel`,
    lastDataLabel: t(i18n)`última alerta de nivel`,
    category: 'advanced',
  },
  [MetricField.LevelRaw]: {
    value: MetricField.LevelRaw,
    dataLabel: t(i18n)`nivel bruto`,
    lastDataLabel: t(i18n)`último nivel bruto`,
    category: 'advanced',
  },
  [MetricField.OriginalDistance]: {
    value: MetricField.OriginalDistance,
    dataLabel: t(i18n)`distancia original`,
    lastDataLabel: t(i18n)`última distancia original`,
    category: 'advanced',
  },
  [MetricField.OriginalDistanceRaw]: {
    value: MetricField.OriginalDistanceRaw,
    dataLabel: t(i18n)`distancia original bruta`,
    lastDataLabel: t(i18n)`última distancia original bruta`,
    category: 'advanced',
  },
  [MetricField.OriginalVol]: {
    value: MetricField.OriginalVol,
    dataLabel: t(i18n)`volumen original`,
    lastDataLabel: t(i18n)`último volumen original`,
    category: 'advanced',
  },
  [MetricField.OriginalVolRaw]: {
    value: MetricField.OriginalVolRaw,
    dataLabel: t(i18n)`volumen original bruto`,
    lastDataLabel: t(i18n)`último volumen original bruto`,
    category: 'advanced',
  },
  [MetricField.Orp]: {
    value: MetricField.Orp,
    dataLabel: t(i18n)`orp`,
    lastDataLabel: t(i18n)`último orp`,
    category: 'basic',
  },
  [MetricField.OrpRaw]: {
    value: MetricField.OrpRaw,
    dataLabel: t(i18n)`orp bruto`,
    lastDataLabel: t(i18n)`último orp bruto`,
    category: 'advanced',
  },
  [MetricField.Ph]: {
    value: MetricField.Ph,
    dataLabel: t(i18n)`ph`,
    lastDataLabel: t(i18n)`último ph`,
    category: 'basic',
  },
  [MetricField.PhRaw]: {
    value: MetricField.PhRaw,
    dataLabel: t(i18n)`ph bruto`,
    lastDataLabel: t(i18n)`último ph bruto`,
    category: 'advanced',
  },
  [MetricField.Position]: {
    value: MetricField.Position,
    dataLabel: t(i18n)`posición`,
    lastDataLabel: t(i18n)`última posición`,
    category: 'advanced',
  },
  [MetricField.RatingCurvesLimits]: {
    value: MetricField.RatingCurvesLimits,
    dataLabel: t(i18n)`límites de curvas de aforo`,
    lastDataLabel: t(i18n)`último límite de curva de aforo`,
    category: 'advanced',
  },
  [MetricField.SignalNoiseRatio]: {
    value: MetricField.SignalNoiseRatio,
    dataLabel: t(i18n)`relación señal-ruido`,
    lastDataLabel: t(i18n)`última relación señal-ruido`,
    category: 'advanced',
  },
  [MetricField.SignalQuality]: {
    value: MetricField.SignalQuality,
    dataLabel: t(i18n)`calidad de señal`,
    lastDataLabel: t(i18n)`última calidad de señal`,
    category: 'advanced',
  },
  [MetricField.SignalStrength]: {
    value: MetricField.SignalStrength,
    dataLabel: t(i18n)`intensidad de señal`,
    lastDataLabel: t(i18n)`última intensidad de señal`,
    category: 'advanced',
  },
  [MetricField.SnowLevel]: {
    value: MetricField.SnowLevel,
    dataLabel: t(i18n)`nivel de nieve`,
    lastDataLabel: t(i18n)`último nivel de nieve`,
    category: 'basic',
  },
  [MetricField.SnowLevelNonCalibrated]: {
    value: MetricField.SnowLevelNonCalibrated,
    dataLabel: t(i18n)`nivel de nieve no calibrado`,
    lastDataLabel: t(i18n)`último nivel de nieve no calibrado`,
    category: 'advanced',
  },
  [MetricField.SnowLevelRaw]: {
    value: MetricField.SnowLevelRaw,
    dataLabel: t(i18n)`nivel de nieve bruto`,
    lastDataLabel: t(i18n)`último nivel de nieve bruto`,
    category: 'advanced',
  },
  [MetricField.SnowRadiation]: {
    value: MetricField.SnowRadiation,
    dataLabel: t(i18n)`radiación de nieve`,
    lastDataLabel: t(i18n)`última radiación de nieve`,
    category: 'advanced',
  },
  [MetricField.SnowRadiationRaw]: {
    value: MetricField.SnowRadiationRaw,
    dataLabel: t(i18n)`radiación de nieve bruta`,
    lastDataLabel: t(i18n)`última radiación de nieve bruta`,
    category: 'advanced',
  },
  [MetricField.SnowTemperature]: {
    value: MetricField.SnowTemperature,
    dataLabel: t(i18n)`temperatura de nieve`,
    lastDataLabel: t(i18n)`última temperatura de nieve`,
    category: 'advanced',
  },
  [MetricField.SnowTemperatureRaw]: {
    value: MetricField.SnowTemperatureRaw,
    dataLabel: t(i18n)`temperatura de nieve bruta`,
    lastDataLabel: t(i18n)`última temperatura de nieve bruta`,
    category: 'advanced',
  },
  [MetricField.SnowWaterEquivalent]: {
    value: MetricField.SnowWaterEquivalent,
    dataLabel: t(i18n)`equivalente de agua de nieve`,
    lastDataLabel: t(i18n)`último equivalente de agua de nieve`,
    category: 'advanced',
  },
  [MetricField.SnowWaterEquivalentNonCalibrated]: {
    value: MetricField.SnowWaterEquivalentNonCalibrated,
    dataLabel: t(i18n)`equivalente de agua de nieve no calibrado`,
    lastDataLabel: t(i18n)`último equivalente de agua de nieve no calibrado`,
    category: 'advanced',
  },
  [MetricField.SnowWaterEquivalentNonCalibratedRaw]: {
    value: MetricField.SnowWaterEquivalentNonCalibratedRaw,
    dataLabel: t(i18n)`equivalente de agua de nieve no calibrado bruto`,
    lastDataLabel: t(i18n)`último equivalente de agua de nieve no calibrado bruto`,
    category: 'advanced',
  },
  [MetricField.SnowWaterEquivalentRaw]: {
    value: MetricField.SnowWaterEquivalentRaw,
    dataLabel: t(i18n)`equivalente de agua de nieve bruto`,
    lastDataLabel: t(i18n)`último equivalente de agua de nieve bruto`,
    category: 'advanced',
  },
  [MetricField.SnowWeight]: {
    value: MetricField.SnowWeight,
    dataLabel: t(i18n)`peso de nieve`,
    lastDataLabel: t(i18n)`último peso de nieve`,
    category: 'basic',
  },
  [MetricField.SnowWeightRaw]: {
    value: MetricField.SnowWeightRaw,
    dataLabel: t(i18n)`peso de nieve bruto`,
    lastDataLabel: t(i18n)`último peso de nieve bruto`,
    category: 'advanced',
  },
  [MetricField.SolarI]: {
    value: MetricField.SolarI,
    dataLabel: t(i18n)`corriente solar`,
    lastDataLabel: t(i18n)`última corriente solar`,
    category: 'advanced',
  },
  [MetricField.SolarIRaw]: {
    value: MetricField.SolarIRaw,
    dataLabel: t(i18n)`corriente solar bruta`,
    lastDataLabel: t(i18n)`última corriente solar bruta`,
    category: 'advanced',
  },
  [MetricField.SolarP]: {
    value: MetricField.SolarP,
    dataLabel: t(i18n)`potencia solar`,
    lastDataLabel: t(i18n)`última potencia solar`,
    category: 'advanced',
  },
  [MetricField.SolarPRaw]: {
    value: MetricField.SolarPRaw,
    dataLabel: t(i18n)`potencia solar bruta`,
    lastDataLabel: t(i18n)`última potencia solar bruta`,
    category: 'advanced',
  },
  [MetricField.SolarV]: {
    value: MetricField.SolarV,
    dataLabel: t(i18n)`voltaje solar`,
    lastDataLabel: t(i18n)`último voltaje solar`,
    category: 'advanced',
  },
  [MetricField.SolarVRaw]: {
    value: MetricField.SolarVRaw,
    dataLabel: t(i18n)`voltaje solar bruto`,
    lastDataLabel: t(i18n)`último voltaje solar bruto`,
    category: 'advanced',
  },
  [MetricField.StoredVolume]: {
    value: MetricField.StoredVolume,
    dataLabel: t(i18n)`volumen almacenado`,
    lastDataLabel: t(i18n)`último volumen almacenado`,
    category: 'advanced',
  },
  [MetricField.Temperature]: {
    value: MetricField.Temperature,
    dataLabel: t(i18n)`temperatura`,
    lastDataLabel: t(i18n)`última temperatura`,
    category: 'basic',
  },
  [MetricField.TemperatureRaw]: {
    value: MetricField.TemperatureRaw,
    dataLabel: t(i18n)`temperatura bruta`,
    lastDataLabel: t(i18n)`última temperatura bruta`,
    category: 'advanced',
  },
  [MetricField.TurbidityFnu]: {
    value: MetricField.TurbidityFnu,
    dataLabel: t(i18n)`turbidez fnu`,
    lastDataLabel: t(i18n)`última turbidez fnu`,
    category: 'advanced',
  },
  [MetricField.TurbidityFnuRaw]: {
    value: MetricField.TurbidityFnuRaw,
    dataLabel: t(i18n)`turbidez fnu bruta`,
    lastDataLabel: t(i18n)`última turbidez fnu bruta`,
    category: 'advanced',
  },
  [MetricField.TurbidityNtu]: {
    value: MetricField.TurbidityNtu,
    dataLabel: t(i18n)`turbidez ntu`,
    lastDataLabel: t(i18n)`última turbidez ntu`,
    category: 'advanced',
  },
  [MetricField.TurbidityNtuRaw]: {
    value: MetricField.TurbidityNtuRaw,
    dataLabel: t(i18n)`turbidez ntu bruta`,
    lastDataLabel: t(i18n)`última turbidez ntu bruta`,
    category: 'advanced',
  },
  [MetricField.Velocity]: {
    value: MetricField.Velocity,
    dataLabel: t(i18n)`velocidad`,
    lastDataLabel: t(i18n)`última velocidad`,
    category: 'basic',
  },
  [MetricField.VelocityRaw]: {
    value: MetricField.VelocityRaw,
    dataLabel: t(i18n)`velocidad bruta`,
    lastDataLabel: t(i18n)`última velocidad bruta`,
    category: 'advanced',
  },
  [MetricField.VolumeDay]: {
    value: MetricField.VolumeDay,
    dataLabel: t(i18n)`volumen diario`,
    lastDataLabel: t(i18n)`último volumen diario`,
    category: 'basic',
  },
  [MetricField.VolumeHour]: {
    value: MetricField.VolumeHour,
    dataLabel: t(i18n)`volumen horario`,
    lastDataLabel: t(i18n)`último volumen horario`,
    category: 'basic',
  },
  [MetricField.VolumeM3]: {
    value: MetricField.VolumeM3,
    dataLabel: t(i18n)`volumen`,
    lastDataLabel: t(i18n)`último volumen`,
    category: 'advanced',
  },
  [MetricField.VolumeMonth]: {
    value: MetricField.VolumeMonth,
    dataLabel: t(i18n)`volumen mensual`,
    lastDataLabel: t(i18n)`último volumen mensual`,
    category: 'basic',
  },
});

export const getMetricFieldAttributes = (key: MetricField, i18n: I18n): MetricFieldAttributes => {
  const attributesMap = getAttributesMap(i18n);
  if (!(key in attributesMap)) {
    throw new Error(`La clave ${key} no se encuentra en el mapa de atributos de MetricField`);
  }
  return attributesMap[key];
};

export const getAllMetricFieldAttributes = (i18n: I18n): MetricFieldAttributes[] => {
  const attributesMap = getAttributesMap(i18n);
  return Object.values(attributesMap);
};
